import React, { useState, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Input, Modal } from '../../../components/ui';
import {
  DollarSign, Plus, RefreshCw, XCircle, Calendar, CreditCard
} from 'lucide-react';
import { adminService, PagoItem, PagoInput, SuscripcionItem } from '../services/adminService';

/**
 * PagosPage — Historial de pagos de mensualidad por banca.
 * 
 * ADMIN registra pagos: monto, fecha, método, observación.
 * Cada pago está vinculado a una suscripción y banca.
 */
export const PagosPage: React.FC = () => {
  const [pagos, setPagos] = useState<PagoItem[]>([]);
  const [suscripciones, setSuscripciones] = useState<SuscripcionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formSubId, setFormSubId] = useState('');
  const [formBancaId, setFormBancaId] = useState('');
  const [formMonto, setFormMonto] = useState('');
  const [formFecha, setFormFecha] = useState(new Date().toISOString().slice(0, 10));
  const [formMetodo, setFormMetodo] = useState('EFECTIVO');
  const [formObs, setFormObs] = useState('');

  const fetchData = () => {
    setLoading(true);
    setError('');
    Promise.all([
      adminService.getPagos(),
      adminService.getSuscripciones(),
    ])
      .then(([p, s]) => { setPagos(p); setSuscripciones(s); })
      .catch(err => setError(err.message || 'Error cargando datos'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleNew = () => {
    setFormSubId('');
    setFormBancaId('');
    setFormMonto('');
    setFormFecha(new Date().toISOString().slice(0, 10));
    setFormMetodo('EFECTIVO');
    setFormObs('');
    setModalOpen(true);
  };

  const handleSubChange = (subId: string) => {
    setFormSubId(subId);
    const sub = suscripciones.find(s => s.id === subId);
    if (sub) {
      setFormBancaId(sub.banca_id);
      setFormMonto(sub.monto_mensual);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const input: PagoInput = {
        suscripcion_id: formSubId,
        banca_id: formBancaId,
        monto: parseFloat(formMonto) || 0,
        fecha: formFecha,
        metodo: formMetodo,
        observacion: formObs || undefined,
      };
      await adminService.registrarPago(input);
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Error registrando pago');
    } finally {
      setSaving(false);
    }
  };

  const totalPagado = pagos.reduce((sum, p) => sum + parseFloat(p.monto), 0);

  const metodoBadge = (metodo: string) => {
    switch (metodo) {
      case 'EFECTIVO': return <Badge variant="success">Efectivo</Badge>;
      case 'SINPE': return <Badge variant="primary">SINPE</Badge>;
      case 'TRANSFERENCIA': return <Badge variant="secondary">Transferencia</Badge>;
      default: return <Badge variant="secondary">{metodo}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pagos de Suscripción</h1>
          <p className="text-gray-500">Historial de mensualidades recibidas</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Refrescar"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <Button variant="primary" leftIcon={<Plus size={18} />} onClick={handleNew}>
            Registrar Pago
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <XCircle className="text-danger-600" size={20} />
            <p className="text-danger-800">{error}</p>
          </div>
        </div>
      )}

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-success-100 rounded-xl flex items-center justify-center">
              <DollarSign className="text-success-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Recaudado</p>
              <p className="text-2xl font-bold text-success-600">₡{totalPagado.toLocaleString()}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <CreditCard className="text-primary-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pagos Registrados</p>
              <p className="text-2xl font-bold">{pagos.length}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Calendar className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Suscripciones activas</p>
              <p className="text-2xl font-bold">
                {suscripciones.filter(s => s.estado === 'ACTIVA').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabla de pagos */}
      <Card>
        <CardHeader title="Historial de pagos" subtitle={`${pagos.length} pagos registrados`} />
        {loading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="animate-spin text-gray-400" size={32} />
          </div>
        ) : pagos.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <DollarSign className="mx-auto mb-2 text-gray-300" size={48} />
            <p>No hay pagos registrados</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Banca</th>
                  <th className="text-left p-3 font-medium text-gray-600">Fecha</th>
                  <th className="text-right p-3 font-medium text-gray-600">Monto</th>
                  <th className="text-left p-3 font-medium text-gray-600">Método</th>
                  <th className="text-left p-3 font-medium text-gray-600">Observación</th>
                </tr>
              </thead>
              <tbody>
                {pagos.map(pago => (
                  <tr key={pago.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <p className="font-medium">{pago.banca_nombre}</p>
                      <p className="text-xs text-gray-400">{pago.banca_id}</p>
                    </td>
                    <td className="p-3 text-gray-600">{pago.fecha}</td>
                    <td className="p-3 text-right font-bold text-success-700">
                      ₡{parseFloat(pago.monto).toLocaleString()}
                    </td>
                    <td className="p-3">{metodoBadge(pago.metodo)}</td>
                    <td className="p-3 text-gray-500 text-xs">{pago.observacion || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal registrar pago */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Registrar Pago de Mensualidad"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Suscripción</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={formSubId}
              onChange={e => handleSubChange(e.target.value)}
            >
              <option value="">Seleccionar suscripción...</option>
              {suscripciones.map(s => (
                <option key={s.id} value={s.id}>
                  {s.banca_nombre} — {s.plan} ({s.estado})
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Monto (₡)"
              type="number"
              placeholder="50000"
              value={formMonto}
              onChange={e => setFormMonto(e.target.value)}
            />
            <Input
              label="Fecha"
              type="date"
              value={formFecha}
              onChange={e => setFormFecha(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Método de pago</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={formMetodo}
              onChange={e => setFormMetodo(e.target.value)}
            >
              <option value="EFECTIVO">Efectivo</option>
              <option value="SINPE">SINPE</option>
              <option value="TRANSFERENCIA">Transferencia</option>
            </select>
          </div>
          <Input
            label="Observación (opcional)"
            placeholder="Referencia de pago, notas..."
            value={formObs}
            onChange={e => setFormObs(e.target.value)}
          />

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving || !formSubId}>
              {saving ? 'Registrando...' : 'Registrar Pago'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PagosPage;
